// packages/frontend/src/features/imports/GhostRecipeCard.tsx
//
// Library ghost card (Plan 12, Task 10) — renders a GhostImport (see
// useActiveImports.ts) in either the RecipeCard-shaped grid slot or the
// RecipeRow-shaped list slot. Upgrades in place as sourceMeta arrives (the
// hook already folds title/siteName/imageKey into the GhostImport, so this
// component just renders whatever it's handed).
import type { JSX } from "react";
import { useState } from "react";
import { Link } from "react-router";
import type { ImportJobStatus } from "@recipevault/shared";
import { CoverPlaceholder } from "../recipes/CoverPlaceholder.js";
import { photosObjectUrl } from "../recipes/photoUrl.js";
import { dismissFailedGhost, type GhostImport } from "./useActiveImports.js";

const STAGE_LABEL: Partial<Record<ImportJobStatus, string>> = {
  queued: "Queued",
  fetching: "Fetching…",
  extracting: "Extracting…",
  saving: "Saving…",
};

interface GhostCoverProps {
  ghost: GhostImport;
  className: string;
  letterClassName: string;
}

/** Cover image via the previews CDN URL; falls back to CoverPlaceholder on a
 *  null imageKey OR a failed image load (the known preview-403 issue is a
 *  deferred fix — see docs/roadmap/post-review-followups-2026-07-10.md §3 —
 *  this onError fallback just keeps the card usable in the meantime). */
function GhostCover({ ghost, className, letterClassName }: GhostCoverProps): JSX.Element {
  const [broken, setBroken] = useState(false);
  const url = photosObjectUrl(ghost.imageKey);
  const showImage = url != null && !broken;

  if (showImage) {
    return (
      <img
        src={url}
        alt=""
        data-testid="ghost-cover-image"
        className={`${className} object-cover`}
        loading="lazy"
        onError={() => setBroken(true)}
      />
    );
  }
  return (
    <CoverPlaceholder
      seed={ghost.jobId}
      title={ghost.label}
      className={className}
      letterClassName={letterClassName}
    />
  );
}

export interface GhostRecipeCardProps {
  ghost: GhostImport;
  view: "grid" | "list";
}

export function GhostRecipeCard({ ghost, view }: GhostRecipeCardProps): JSX.Element {
  const isOverCap = ghost.status === "over_cap";
  const isFailed = ghost.status === "failed" || isOverCap;
  const isReview = ghost.pendingRecipeId !== null;
  const stageLabel = STAGE_LABEL[ghost.status];

  // Review ghosts skip Detail (a `done` job never auto-redirects there — see
  // ImportDetail's wasDoneOnMountRef guard) and go straight to the review
  // screen. Every other ghost taps to Detail, which owns retry/review routing.
  const to = isReview
    ? `/imports/${encodeURIComponent(ghost.jobId)}/review`
    : `/imports/${encodeURIComponent(ghost.jobId)}`;

  const statusText = isOverCap
    ? "Monthly cap reached"
    : isFailed
      ? "Import failed"
      : isReview
        ? "Review photo import →"
        : (stageLabel ?? "Importing…");

  function handleClick(): void {
    if (isFailed) dismissFailedGhost(ghost.jobId);
  }

  const borderClass = isFailed
    ? "border-red-800/60 bg-red-950/10 hover:border-red-700"
    : isReview
      ? "border-emerald-700/60 bg-emerald-950/10 hover:border-emerald-600"
      : "border-dashed border-zinc-700 bg-zinc-900/40 hover:border-zinc-600";

  const statusClass = isFailed ? "text-red-400" : isReview ? "text-emerald-400" : "text-zinc-500";

  if (view === "list") {
    return (
      <Link
        to={to}
        onClick={handleClick}
        data-testid="ghost-recipe-row"
        className={`group grid grid-cols-[80px_1fr] sm:grid-cols-[80px_1fr_auto] gap-3 sm:gap-4 items-center px-3 py-3 border transition-colors outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/50 ${borderClass}`}
      >
        <GhostCover ghost={ghost} className="w-20 h-20 rounded-md shrink-0" letterClassName="text-2xl" />
        <div className="min-w-0 flex flex-col gap-0.5">
          <h3 className="text-zinc-50 font-medium text-base leading-tight truncate">{ghost.label}</h3>
          {ghost.siteName != null && (
            <p className="text-zinc-400 text-sm truncate">{ghost.siteName}</p>
          )}
          <p className={`text-xs ${statusClass}`}>{statusText}</p>
        </div>
        {isFailed && (
          <span className="hidden sm:inline text-xs text-red-400 shrink-0">
            {isOverCap ? "View cap settings ↗" : "Retry ↗"}
          </span>
        )}
      </Link>
    );
  }

  return (
    <Link
      to={to}
      onClick={handleClick}
      data-testid="ghost-recipe-card"
      className={`group flex flex-col rounded-lg border overflow-hidden transition-colors outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/50 ${borderClass}`}
    >
      <GhostCover ghost={ghost} className="aspect-video w-full" letterClassName="text-6xl" />
      <div className="p-3 flex flex-col gap-1.5">
        <h3 className="text-zinc-50 font-medium text-sm leading-tight line-clamp-2">{ghost.label}</h3>
        {ghost.siteName != null && (
          <p className="text-xs text-zinc-400 truncate">{ghost.siteName}</p>
        )}
        <p className={`text-xs ${statusClass}`}>{statusText}</p>
        {isFailed && (
          <span className="text-xs text-red-400">{isOverCap ? "View cap settings ↗" : "Retry ↗"}</span>
        )}
      </div>
    </Link>
  );
}
